import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';

import loadSale, { saleUrl } from './loadSale';
import groupProducts from './groupProducts';
import { SaleCategory, SaleProduct, SalePayload } from './types';
import { Restrictions } from './restrictions';

/**
 * `over` is not a failure to load — it is the sale being finished, or not started.
 *
 * It is a status rather than a flag beside one because there is no fetch in that state:
 * the legacy page never rendered its sale component at all once the promotion had ended,
 * and neither does this one. Asking for the document anyway would download several
 * hundred products to draw one dialog, and would fill the header's category menu and
 * scoped search with a sale the page is not showing.
 */
export type SaleStatus = 'loading' | 'loaded' | 'error' | 'over';

export type SaleData = {
  categories: SaleCategory[];
  /** Products per category id, already merged into tiles. */
  productsByCategory: Record<number, SaleProduct[]>;
};

/**
 * Where in the sale the page has been asked to go, and what to do on arrival.
 *
 * Carries a request number because the answer to "where" is not enough. Choosing the same
 * suggestion from the search a second time is the same category and the same product, so a
 * selection that was only those two values was not a new value at all — React saw no state
 * change, the scroll effect never ran again, and the page sat where the customer had
 * scrolled it to. Every `select()` bumps this, so a repeat choice is a fresh request.
 */
export type SaleSelection = {
  /** The category the page should scroll to and render eagerly, if any. */
  categoryId: number | null;
  /** The product within it, when the request names one. */
  productId: number | null;
  /**
   * Whether arriving should also open the product's detail view.
   *
   * The scoped search sets it on a desktop and clears it on a phone, which is the legacy
   * page's rule: its `typeahead:selected` scrolled, and then opened the detail only when
   * `Responsive.isMobileView()` was false. A phone has no room to scroll a customer to a
   * product and then cover it with a dialog of the same product.
   */
  openDetail: boolean;
  request: number;
};

export type SelectOptions = {
  openDetail?: boolean;
};

export type SaleContextValue = {
  status: SaleStatus;
  data: SaleData | null;
  error: string | null;
  retry: () => void;

  /** Where the page has been asked to go. */
  selection: SaleSelection;
  /**
   * Points the page at a product without a navigation — what the scoped search does
   * when a suggestion is chosen, and what the category menu does.
   */
  select: (categoryId: number | null, productId?: number | null, options?: SelectOptions) => void;

  /** The right-drawer cart. */
  cartDrawer: { isOpen: boolean; open: () => void; close: () => void; toggle: () => void };

  /** The "add one more as a gift" offer that follows an add to cart. */
  giftOffer: GiftOfferState;

  /**
   * What this visitor may not be shown.
   *
   * Published *up* by the page rather than passed down, which is the wrong direction
   * only if you read the layout as the owner of everything. The restrictions are the
   * visitor's, so they arrive on the page's props and must never reach a cached layout
   * render — but the header's search box has to honour them too, or typing a title is a
   * way around the restriction. This is how the one crosses to the other.
   *
   * Empty until the page publishes, which hides everything rather than nothing: the
   * index has no entries yet, so no suggestion can leak.
   */
  restrictions: Restrictions;
  publishRestrictions: (restrictions: Restrictions) => void;
};

/**
 * What was just added, and whether we are offering a second copy of it as a gift.
 *
 * One offer at a time, and one per product per visit: the second add of a title clears
 * the offer instead of making it again, which is what the legacy component's
 * `giftLightboxSkipProductIds` did and is the difference between an upsell and nagging.
 *
 * It is here rather than in the page because it is the cart drawer that draws it and the
 * page that raises it.
 */
export type GiftOfferState = {
  product: SaleProduct | null;
  /** The sku a gift copy would add: the same one just bought, sent as a gift. */
  skuCode: string | null;
  offerCode: string | null;
  /**
   * The cart line the add produced.
   *
   * The offer hangs under that exact line and under no other. It used to fall back to the
   * top of the list when the line was gone, which turned "this line has been removed" into
   * "show the offer against something else" — remove the product you just added and an
   * offer reappeared over a different one. An offer with no live line of its own is an
   * offer that has been answered.
   */
  cartItemId: number | null;
  offer: (product: SaleProduct, skuCode: string, offerCode: string | null, cartItemId: number) => void;
  dismiss: () => void;
};

const SaleContext = createContext<SaleContextValue | null>(null);

const EMPTY_RESTRICTIONS: Restrictions = { restrictedProductIds: [], hotzoneProductIds: [] };

function sameRestrictions(a: Restrictions, b: Restrictions): boolean {
  return (
    a.restrictedProductIds.length === b.restrictedProductIds.length &&
    a.hotzoneProductIds.length === b.hotzoneProductIds.length &&
    a.restrictedProductIds.every((id, index) => id === b.restrictedProductIds[index]) &&
    a.hotzoneProductIds.every((id, index) => id === b.hotzoneProductIds[index])
  );
}

export type SaleProviderProps = {
  /**
   * The sale document's full url, decided on the server (SaleDocumentUrl): the CDN on
   * DiscountMags in production, the site's own origin everywhere else.
   */
  saleDocumentUrl: string;
  /** `xEO=Y` — render the sale even though it has expired. */
  previewMode: boolean;
  /**
   * The sale has ended, or has not started yet — so there is nothing to load.
   *
   * The server has already applied `xEO=Y` by the time this arrives: an override turns
   * the sale back on, and this is false again.
   */
  saleOver?: boolean;
  initialCategoryId?: number | null;
  initialProductId?: number | null;
  children: ReactNode;
};

/**
 * Holds the sale.
 *
 * It lives in the *layout* rather than in the page because both need it: the page draws
 * the products, and the header draws this sale's categories in the mobile menu and
 * scopes its search box to these products. tree.tsx composes layout-around-page, so the
 * layout is the only place one copy of this can serve both — and one copy is the point,
 * since the alternative is two components fetching the same JSON.
 *
 * Nothing here is per-visitor, so none of it is a reason to skip caching the markup.
 * The restrictions, which are, stay on the page's own props.
 */
export function SaleProvider({
  saleDocumentUrl,
  previewMode,
  saleOver = false,
  initialCategoryId = null,
  initialProductId = null,
  children,
}: SaleProviderProps) {
  const [status, setStatus] = useState<SaleStatus>(saleOver ? 'over' : 'loading');
  const [data, setData] = useState<SaleData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // The url's own selection is request zero. A deep link opens the product's detail view,
  // which is what the legacy component did with `showModal: !!props.selected`.
  const [selection, setSelection] = useState<SaleSelection>({
    categoryId: initialCategoryId,
    productId: initialProductId,
    openDetail: initialProductId !== null,
    request: 0,
  });

  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [giftOfferTarget, setGiftOfferTarget] = useState<{
    product: SaleProduct | null;
    skuCode: string | null;
    offerCode: string | null;
    cartItemId: number | null;
  }>({ product: null, skuCode: null, offerCode: null, cartItemId: null });

  // Which titles have already been offered as a gift. A ref rather than state: nothing
  // renders from it, and putting it in state would re-render every tile on each add.
  const offeredProductIds = useRef<Set<number>>(new Set());

  const [restrictions, setRestrictions] = useState<Restrictions>(EMPTY_RESTRICTIONS);

  useEffect(() => {
    if (saleOver) {
      return;
    }

    const controller = new AbortController();

    setStatus('loading');
    setError(null);

    loadSale(saleUrl(saleDocumentUrl, previewMode), controller.signal)
      .then((payload: SalePayload) => {
        if (controller.signal.aborted) {
          return;
        }

        setData(toSaleData(payload));
        setStatus('loaded');
      })
      .catch((failure: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        // Reported rather than swallowed. The legacy component logged to the console and
        // nothing else, so a sale that would not load was invisible unless somebody
        // happened to be looking at their own devtools.
        console.error('Could not load the sale', failure);
        setError(failure instanceof Error ? failure.message : String(failure));
        setStatus('error');
      });

    return () => controller.abort();
  }, [saleDocumentUrl, previewMode, saleOver, attempt]);

  const select = useCallback(
    (categoryId: number | null, productId: number | null = null, options: SelectOptions = {}) => {
      setSelection((current) => ({
        categoryId,
        productId,
        openDetail: options.openDetail ?? false,
        request: current.request + 1,
      }));
    },
    []
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Replaced rather than merged, and compared by content: the page rebuilds the object
  // on every render from props that never change, so an identity check would set state
  // in a loop.
  const publishRestrictions = useCallback((next: Restrictions) => {
    setRestrictions((current) => (sameRestrictions(current, next) ? current : next));
  }, []);

  const cartDrawer = useMemo(
    () => ({
      isOpen: cartDrawerOpen,
      open: () => setCartDrawerOpen(true),
      close: () => setCartDrawerOpen(false),
      toggle: () => setCartDrawerOpen((open) => !open),
    }),
    [cartDrawerOpen]
  );

  const giftOffer = useMemo<GiftOfferState>(
    () => ({
      ...giftOfferTarget,
      offer: (product, skuCode, offerCode, cartItemId) =>
        setGiftOfferTarget(() => {
          // Offered once per title. A customer who has already seen it for this product
          // and added it again has answered; asking a second time is nagging.
          if (offeredProductIds.current.has(product.id)) {
            return { product: null, skuCode: null, offerCode: null, cartItemId: null };
          }

          offeredProductIds.current.add(product.id);

          return { product, skuCode, offerCode, cartItemId };
        }),
      dismiss: () => setGiftOfferTarget({ product: null, skuCode: null, offerCode: null, cartItemId: null }),
    }),
    [giftOfferTarget]
  );

  const value = useMemo<SaleContextValue>(
    () => ({
      status,
      data,
      error,
      retry,
      selection,
      select,
      cartDrawer,
      giftOffer,
      restrictions,
      publishRestrictions,
    }),
    [
      status,
      data,
      error,
      retry,
      selection,
      select,
      cartDrawer,
      giftOffer,
      restrictions,
      publishRestrictions,
    ]
  );

  return <SaleContext.Provider value={value}>{children}</SaleContext.Provider>;
}

export function useSale(): SaleContextValue {
  const value = useContext(SaleContext);

  if (!value) {
    throw new Error('useSale must be used inside a SaleProvider — see Layouts.MegaSale');
  }

  return value;
}

/**
 * Groups the flat product list by category and merges grouped products into tiles.
 *
 * Done once, here, rather than per render: a sale can carry several hundred products
 * and the grouping walks the list for each of them.
 */
function toSaleData(payload: SalePayload): SaleData {
  const productsByCategory: Record<number, SaleProduct[]> = {};

  payload.products.forEach((product) => {
    const id = product.category.id;

    (productsByCategory[id] ||= []).push(product);
  });

  Object.keys(productsByCategory).forEach((id) => {
    const key = Number(id);

    productsByCategory[key] = groupProducts(productsByCategory[key]) ?? [];
  });

  // A category with no products of its own draws nothing, so it is dropped here rather
  // than left to render an empty heading and an empty entry in the menu and the nav.
  const categories = payload.categories.filter(
    (category) => (productsByCategory[category.id] || []).length > 0
  );

  return { categories, productsByCategory };
}
